import { FC, memo, useContext } from 'react';
import { Polyline } from 'react-leaflet';
import LicenseContext from 'src/contexts/LicenseContext';
import { EditPlaceEntity } from 'src/entities/EditPlace.entity';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { PositionEntity } from 'src/entities/PositionEntity';
import { SelectedCycle } from 'src/entities/SelectedCycle.entity';
import { PlanningsOperationDelivery } from 'src/models/PlanningsOperationGroup.model';
import { datetimeUtil } from 'src/utils/datetime.util';

import { planningDeliveryColor } from '../planningColors';

type Props = {
  editPlaces: EditPlaceEntity[];
  delivery: PlanningsDeliveryEntity;
  selectedCycleIndexes?: SelectedCycle;
  planningsOperationDelivery: PlanningsOperationDelivery;
  deliveryEntities: PlanningsDeliveryEntity[];
}

const positionFromStrings = (latitude: string, longitude: string): PositionEntity | undefined => {
  const position: PositionEntity = [parseFloat(latitude), parseFloat(longitude)];
  if (!position.every((it) => Number.isFinite(it))) return undefined;

  return position;
};

export const buildPlanningMapDeliveryPolylinePositions = ({
  editPlaces,
  delivery,
  selectedCycleIndexes,
  planningsOperationDelivery,
  deliveryEntities,
  useTrunkTransportation,
}: Props & {
  useTrunkTransportation?: boolean;
}): PositionEntity[] => {
  if (!delivery || !planningsOperationDelivery || !selectedCycleIndexes) return [];

  const garagePosition = positionFromStrings(delivery.garageLatitude, delivery.garageLongitude);
  const plotPositions: PositionEntity[] = [];
  const filledCycleIndexes = planningsOperationDelivery.cycles.filter((it) => !it.empty).map((it) => it.cycleIndex);

  planningsOperationDelivery.cycles.forEach((cycle) => {
    let operations = cycle.allOperations();

    if (editPlaces) {
      const editPlace = editPlaces.find((it) => it.deliveryId === delivery.id && it.cycleIndex === cycle.cycleIndex);
      if (editPlace) {
        operations = editPlace.places.flatMap((it) => it.allOperations());
      }
    }
    if (operations.length === 0) { return; }
    if (!selectedCycleIndexes.cycleIndexes.includes(cycle.cycleIndex)) { return; }

    const firstSelectedCycle = cycle.cycleIndex === Math.min(...filledCycleIndexes);
    const lastSelectedCycle = cycle.cycleIndex === Math.max(...filledCycleIndexes);
    const shouldStartFromGarage = !!delivery.departureOperation || delivery.driverId == null;
    const shouldReturnToGarage = !!delivery.arrivalOperation || (delivery.driverId == null && delivery.returnToBase);

    if (garagePosition && shouldStartFromGarage && firstSelectedCycle) {
      plotPositions.push(garagePosition);
    }
    operations.forEach((ops) => {
      const position = positionFromStrings(ops.latitude, ops.longitude);
      if (position) plotPositions.push(position);
    });
    if (shouldReturnToGarage) {
      if (useTrunkTransportation) {
        const sorted = [...operations].sort((a, b) => new Date(b.departureAt).getTime() - new Date(a.departureAt).getTime());
        const isLastDay = datetimeUtil.isSameDay(new Date(delivery.endAt), new Date(sorted[0].departureAt));
        if (garagePosition && isLastDay) {
          plotPositions.push(garagePosition);
        }
      } else if (garagePosition && lastSelectedCycle) {
        plotPositions.push(garagePosition);
      }
    } else {
      // 車庫に帰らない場合、次の勤務を探してそこまでの線を引く。
      const nextDelivery = deliveryEntities
        .filter((it) => it.driverId === delivery.driverId && it.truckId === delivery.truckId && it.id !== delivery.id && new Date(it.startAt).getTime() > new Date(delivery.startAt).getTime())
        .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime())[0];
      if (nextDelivery && nextDelivery.operations.length > 0) {
        const firstOperation = [...nextDelivery.operations].sort((a, b) => new Date(a.arrivalAt).getTime() - new Date(b.arrivalAt).getTime())[0];
        const position = positionFromStrings(firstOperation.latitude, firstOperation.longitude);
        if (position) plotPositions.push(position);
      }
    }
  });

  return plotPositions;
};

const PlanningMapDeliveryPolylinesPresenter: FC<Props> = memo((
  {
    editPlaces,
    delivery,
    selectedCycleIndexes,
    planningsOperationDelivery,
    deliveryEntities,
  }
) => {
  const licenseContext = useContext(LicenseContext);
  if (!delivery || !planningsOperationDelivery) return null;

  const plotPositions = buildPlanningMapDeliveryPolylinePositions({
    editPlaces,
    delivery,
    selectedCycleIndexes,
    planningsOperationDelivery,
    deliveryEntities,
    useTrunkTransportation: licenseContext.config?.use_trunk_transportation,
  });
  if (plotPositions.length < 2) return null;

  return (
    <Polyline
      positions={plotPositions}
      pathOptions={{ color: planningDeliveryColor(delivery) }}
    />
  );
});

export default PlanningMapDeliveryPolylinesPresenter;
